import { useCallback, useContext, useEffect, useMemo } from 'react'

import { EDITOR_REQUIRED_ANCHORS } from '@editor/config/constants'
import { CursorState } from '@editor/config/constants/cursor'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { EditorButtons, EditorConfig } from '@editor/interfaces/editor'
import { BlueprintEditorContextState } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import { BlueprintEditorTools, Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import useConfigureCanvas from '@editor/pages/projects/blueprint-editor/hooks/useConfigureCanvas'
import {
  setMode,
  setSelectedTool,
  updateBlueprint,
} from '@editor/pages/projects/blueprint-editor/store/blueprintEditor'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { resetWorking, setIsSaving, setMaskRegions } from '@editor/pages/projects/editor/tools/MaskPCD/store'
import { COMMON_MESSAGE } from '@editor/services/ErrorHandler'
import { withPromiseContained } from '@editor/services/Util'
import { GenericError } from '@editor/services/errors/GenericError'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCursor } from '@editor/store/tool'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'

/**
 * Configure the editor for PCD Trim tool.
 */
export default function useEditor(context: BlueprintEditorContextState): EditorConfig {
  // Hooks
  const { setupControls, orientCamera } = useConfigureCanvas(context)

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const areaSwitched = useSelector((state: RootState) => state.page.areaSwitched)

  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const prevSelectedTool = useSelector((state: RootState) => state.blueprint.editor.prevSelectedTool)
  const selectedBlueprint = useSelector((state: RootState) => state.blueprint.editor.selectedBlueprint)
  const isCreatingBlueprint = useSelector((state: RootState) => state.blueprint.editor.isCreatingBlueprint)

  const workingMaskCuboid = useSelector((state: RootState) => state.maskPCD.workingMaskCuboid)
  const workingMask = useSelector((state: RootState) => state.maskPCD.workingMask)
  const isSaving = useSelector((state: RootState) => state.maskPCD.isSaving)

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useContext(GlobalModalContext)

  // Vars
  const isToolSelected = useMemo(() => selectedTool === BlueprintEditorTools.MaskPCD, [selectedTool])
  const isPreviousTool = useCallback((tool: BlueprintEditorTools) => prevSelectedTool === tool, [prevSelectedTool])

  /**
   * Load blueprint's masking region into store when selected, and reset when unselected.
   */
  useEffect(() => {
    if (selectedBlueprint?.masking_region) {
      dispatch(
        setMaskRegions([
          {
            region_id: selectedBlueprint.blueprint_id,
            ...selectedBlueprint.masking_region,
          },
        ]),
      )
    } else {
      dispatch(setMaskRegions([]))
    }
  }, [selectedBlueprint?.masking_region, selectedBlueprint?.blueprint_id, dispatch])

  /**
   * Show attention text when tool is activated.
   */
  useEffect(() => {
    if (selectedTool === BlueprintEditorTools.MaskPCD && prevSelectedTool !== BlueprintEditorTools.Focus) {
      dispatch(
        setAttentionText({
          message:
            '注目領域内の点のみを表示します。領域外の点は非表示になります（再表示できます）。\n' +
            '注目領域を作成するため、3点を選んで面を選択してください。その後、1点を選んで奥行きを設定してください。\n' +
            '調整完了後、右下の保存ボタンを押してください。',
          linkMessage: '詳細はこちら（操作動画）',
          linkUrl: 'https://datalabs-jp.notion.site/a176ae9a89b94aa1889f10c57742d862',
        }),
      )
      dispatch(setMode(Mode.Editor3D))
      setupControls(Mode.Editor3D)
      orientCamera(Mode.Editor3D)
    } else if (
      isPreviousTool(BlueprintEditorTools.MaskPCD) &&
      selectedTool !== BlueprintEditorTools.Focus &&
      !isCreatingBlueprint
    ) {
      dispatch(setMode(Mode.Editor2D))
      setupControls(Mode.Editor2D)
      orientCamera(Mode.Editor2D)
    }
  }, [dispatch, isPreviousTool, orientCamera, setupControls, selectedTool, isCreatingBlueprint, prevSelectedTool])

  /**
   * On change to different tool, reset the processing anchor.
   */
  useEffect(() => {
    if (isToolSelected) {
      dispatch(setCursor(CursorState.CROSSHAIR))
    } else if (
      !isToolSelected &&
      isPreviousTool(BlueprintEditorTools.BlueprintManager) &&
      selectedTool !== BlueprintEditorTools.Focus
    ) {
      dispatch(resetWorking())
      dispatch(setCursor(CursorState.DEFAULT))
    }
  }, [isToolSelected, selectedTool, isPreviousTool, dispatch])

  /**
   * Reset on unmount or  when area is switched.
   */
  useEffect(() => {
    dispatch(resetWorking())

    return () => {
      dispatch(resetWorking())
    }
  }, [areaSwitched, dispatch])

  /**
   * Button behavior differs based on when the mask is being created or modified.
   * - When creating, button is "Save" and will save the new mask to the
   */
  const buttons = useMemo<EditorButtons>(() => {
    if (isCreatingBlueprint && isToolSelected) {
      return {
        submit: {
          key: 'save-mask-region',
          label: '確定',
          onClick: () => {
            dispatch(setSelectedTool(BlueprintEditorTools.BlueprintManager))
            dispatch(setAttentionText({ message: '' }))
          },
          isShown: () => true,
          isDisabled: () =>
            !workingMaskCuboid || workingMaskCuboid.points.length < EDITOR_REQUIRED_ANCHORS.cuboid || isSaving,
        },
        reset: {
          onClick: () => {
            dispatch(resetWorking())
          },
          isShown: () => true,
          isDisabled: () => !workingMaskCuboid?.points.length || isSaving,
        },
      }
    }

    return {
      submit: {
        key: 'save-mask-region',
        label: '保存',
        loadingLabel: '保存中',
        onClick: async () => {
          if (!workingMask || !project || !inspectionArea || !selectedBlueprint) {
            return
          }

          dispatch(setIsSaving(true))

          await withPromiseContained(async () => {
            const token = await getAccessToken()
            if (!token) return

            dispatch(setIsSaving(true))
            dispatch(setAttentionText({ message: '注目領域を保存中...' }))

            const result = await dispatch(
              updateBlueprint({
                accessToken: token,
                projectId: project.project_id,
                areaId: inspectionArea.inspection_area_id,
                blueprint: {
                  ...selectedBlueprint,
                  masking_region: {
                    center: workingMask.center,
                    rotation: workingMask.rotation,
                    extent: workingMask.extent,
                    use_mask: true,
                  },
                },
                updateMaskingRegion: true,
              }),
            )
            if (!result) return

            mixpanel.track('Saved mask region')
          })
            .catch((err) => {
              showErrorModal(err instanceof GenericError ? err.message : COMMON_MESSAGE)
            })
            .finally(() => {
              dispatch(setAttentionText({ message: '' }))
              dispatch(setIsSaving(false))
              dispatch(resetWorking())
              dispatch(setSelectedTool(BlueprintEditorTools.Selector))
            })
        },
        isShown: () => isToolSelected,
        isLoading: () => isSaving,
        isDisabled: () =>
          !workingMaskCuboid?.points.length ||
          workingMaskCuboid.points.length < EDITOR_REQUIRED_ANCHORS.cuboid ||
          isSaving,
      },
      reset: {
        onClick: () => dispatch(resetWorking()),
        isShown: () => isToolSelected,
        isDisabled: () => !workingMaskCuboid?.points.length || isSaving,
      },
    }
  }, [
    project,
    inspectionArea,
    selectedBlueprint,
    isCreatingBlueprint,
    isToolSelected,
    workingMaskCuboid,
    workingMask,
    isSaving,
    dispatch,
    getAccessToken,
    showErrorModal,
  ])

  return {
    buttons,
  }
}
